import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import {
  getCurrentBehaviourForecast,
  getTariffsTOUCosts,
  getUserGridCampaigns,
  getUserGridEvents,
  tdasCampaignId,
} from '../../api/Optimise';
import { AppContext } from '../../context/AppContext';
import withPageTitle from '../../hocs/withPageTitle';
import { useHandleModalPath } from '../../hooks';
import { DisabledWarning } from '../../ui';
import FurtherInfo from '../../ui/Dashboard/Today/FurtherInfo';
import RecentBatteryStatus from '../../ui/Dashboard/Today/RecentBatteryStatus';
import TodaySoFar from '../../ui/Dashboard/Today/TodaySoFar';
import TurnDownAndSave from '../../ui/Dashboard/Today/TurnDownAndSave';
import LoadingCard from '../../ui/LoadingCard';
import InfoModal from '../../ui/Modal/InfoModal';
import TdasModal, { MODAL_VIEWS } from '../../ui/Modal/TdasModal';
import SmartTariffWarning from '../../ui/SmartTariffWarning';
import WarningPanel from '../../ui/WarningPanel';
import aggregateRecords from '../../utils/aggregateRecords';
import {
  addHours,
  addMinutes,
  formatHHmmInTz,
  fromUnixTime,
  getUnixTime,
  isAfter,
  isBefore,
  normalizeToUnixSeconds,
  nowInTz,
  SITE_TIMEZONE,
  startOfDayInTz,
  subHours,
} from '../../utils/dateUtils';
import getLoopIsDisabled from '../../utils/getLoopIsDisabled';
import getNextFutureEvent from '../../utils/getNextFutureEvent';
import { processForecastForChart, segmentByActivity } from '../../utils/socChartUtils';

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TodayContainer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    hideTdasBanner,
    setHideTdasBanner,
    userSub,
    showTdasModal,
    setShowTdasModal,
    setHasShownTdasModal,
    setTdasModalView,
    userMpan,
    tdasIsActive,
    setUserCampaigns,
    setGridEvent,
    setUserEvents,
    showSimulationMode,
    userDetails,
    twentyFourHoursOfData,
    fetchingTwentyFourHoursOfData,
    latestRecord,
  } = useContext(AppContext);
  const [behaviourForecast, setBehaviourForecast] = useState(null);
  const [lastInstruction, setLastInstruction] = useState(null);
  const [nextInstruction, setNextInstruction] = useState(null);
  const [behaviorChartData, setBehaviourChartData] = useState(null);
  const [futureRecords, setFutureRecords] = useState(null);
  const [historicChartData, setHistoricChartData] = useState(null);
  const [todaySoFarData, setTodaySoFarData] = useState(null);
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  const [fetchingBehaviourForecast, setFetchingBehaviourForecast] = useState(false);
  const [costWindows, setCostWindows] = useState([]);
  const [costWindowsExport, setCostWindowsExport] = useState([]);
  const [futureChartSegments, setFutureChartSegments] = useState([]);
  const [hasTariffFetchError, setHasTariffFetchError] = useState(false);

  const loopIsDisabled = getLoopIsDisabled(showSimulationMode, userDetails);
  const isLoadingUserDetails = !userDetails;

  const onOpenTdasModal = useCallback(
    (nextView = MODAL_VIEWS.INTRO) => {
      setShowTdasModal(true);
      setHasShownTdasModal(true);
      setTdasModalView(nextView);
    },
    [setShowTdasModal, setHasShownTdasModal, setTdasModalView]
  );

  useEffect(() => {
    const asyncUseEffect = async () => {
      setFetchingBehaviourForecast(true);
      const data = await getCurrentBehaviourForecast(userSub).catch((error) => {
        console.error('Error fetching current behaviour forecast:', error);
      });
      setFetchingBehaviourForecast(false);
      const timeThreshold = new Date();

      // Check if the API returned any records that are genuinely in the future
      // (used to decide whether to show fallback data).
      // We store ALL records so the chart can include the gap between the last
      // real data point and wall-clock now when data is stale.
      const hasFutureRecords = data?.some((record) => {
        const recordTime = fromUnixTime(record.target_datetime_utc);
        return isAfter(recordTime, timeThreshold);
      });

      if (hasFutureRecords) {
        setBehaviourForecast(data);
        setLastInstruction(data[0]);
        setNextInstruction(data[1]);
      } else {
        // Generate fallback dataset.
        // Use new Date() (UTC epoch time) for arithmetic.
        // nowInTz gives local minutes for :00/:30 alignment, but its epoch is timezone-shifted;
        // keep the UTC anchor separate so getTime()/addMinutes math stays stable.
        const actualNow = new Date();
        const localNow = nowInTz(SITE_TIMEZONE);

        // Align start to next :00 or :30
        const minutes = localNow.getMinutes();
        const start = minutes < 30 ? addMinutes(actualNow, 30 - minutes) : addMinutes(addHours(actualNow, 1), -minutes);

        const totalIntervals = 25; // 12.5 hours in half hour increments
        const fallbackRecords = [];

        for (let i = 0; i < totalIntervals; i++) {
          fallbackRecords.push({
            target_datetime_utc: getUnixTime(new Date(start.getTime() + i * 30 * 60 * 1000)),
            value: null,
          });
        }

        setBehaviourForecast(fallbackRecords);
      }
    };

    asyncUseEffect();
  }, [userSub]);

  // Fetch tariff cost windows for the chart time range
  useEffect(() => {
    const fetchTariffCosts = async () => {
      const tariffId = userDetails?.tariffs?.import_tariff?.id;

      if (!tariffId) {
        setCostWindows([]);
        return;
      }

      try {
        const now = new Date();
        const startTimestamp = getUnixTime(subHours(now, 12));
        const endTimestamp = getUnixTime(addHours(now, 12));

        const touCosts = await getTariffsTOUCosts(startTimestamp, endTimestamp, tariffId);

        setCostWindows(touCosts?.cost_windows || []);
      } catch (error) {
        console.error('Error fetching tariff costs:', error);
        setCostWindows([]);
        setHasTariffFetchError(true);
      }
    };

    fetchTariffCosts();
  }, [userDetails?.tariffs?.import_tariff?.id]);

  // Fetch export tariff cost windows for the chart (for "Show export rates" toggle)
  useEffect(() => {
    const fetchExportTariffCosts = async () => {
      const tariffId = userDetails?.tariffs?.export_tariff?.id;

      if (!tariffId) {
        setCostWindowsExport([]);
        return;
      }

      try {
        const now = new Date();
        const startTimestamp = getUnixTime(subHours(now, 12));
        const endTimestamp = getUnixTime(addHours(now, 12));
        const touCosts = await getTariffsTOUCosts(startTimestamp, endTimestamp, tariffId);

        setCostWindowsExport(touCosts?.cost_windows || []);
      } catch (error) {
        console.error('Error fetching export tariff costs:', error);
        setCostWindowsExport([]);
        setHasTariffFetchError(true);
      }
    };

    fetchExportTariffCosts();
  }, [userDetails?.tariffs?.export_tariff?.id]);

  useEffect(() => {
    if (fetchingBehaviourForecast || fetchingTwentyFourHoursOfData) {
      return;
    }
    if (!!twentyFourHoursOfData && !!behaviourForecast) {
      const now = new Date();
      const twelveHoursAgo = subHours(now, 12);
      const twelveHoursLater = addHours(now, 12);

      // build data for chart
      // Use the last historic data point as the forecast cutoff rather than
      // wall-clock now — if data is stale the forecast should still cover
      // the gap between the last update and the actual current time.
      const lastHistoricTs = [...(twentyFourHoursOfData ?? [])]
        .map((row) => normalizeToUnixSeconds(row.end_date))
        .filter(Number.isFinite)
        .reduce((max, ts) => Math.max(max, ts), 0);
      const forecastFrom = lastHistoricTs > 0 ? fromUnixTime(lastHistoricTs) : now;

      const futureRecords = behaviourForecast?.filter((row) => {
        const itemDate = fromUnixTime(row.target_datetime_utc);
        return isAfter(itemDate, forecastFrom) && isBefore(itemDate, twelveHoursLater);
      });

      const sortedFutureRecords = (futureRecords ?? []).toSorted(
        (a, b) => (a?.target_datetime_utc ?? 0) - (b?.target_datetime_utc ?? 0)
      );

      setFutureRecords(sortedFutureRecords);

      // Process future records with tariff data for enriched tooltips and activity types
      const futureChartData = processForecastForChart(sortedFutureRecords, costWindows, SITE_TIMEZONE);

      // Add previous SOC to each point for tooltip kWh calculation
      const futureChartDataWithPrev = futureChartData.map((point, i) => ({
        ...point,
        _previousSoc: i > 0 ? futureChartData[i - 1].y : point.y,
      }));

      // Segment future data by activity type for colour-coded chart lines
      const segments = segmentByActivity(futureChartDataWithPrev);
      setFutureChartSegments(segments);

      const startOfToday = startOfDayInTz(SITE_TIMEZONE, now);

      // Filter records from today for Today so far box
      const pastRecordsToday = twentyFourHoursOfData?.filter((row) => {
        const itemDate = fromUnixTime(row.end_date);
        return !isBefore(itemDate, startOfToday);
      });

      // Filter the last twelve hours for the chart
      const pastRecords = twentyFourHoursOfData?.filter((row) => {
        const itemDate = fromUnixTime(row.end_date);
        return isAfter(itemDate, twelveHoursAgo) && isBefore(itemDate, now);
      });

      const sortedPastRecords = (pastRecords ?? []).toSorted(
        (a, b) => Number(a?.end_date ?? 0) - Number(b?.end_date ?? 0)
      );

      const historicChartData = sortedPastRecords.map((row) => ({
        data: row,
        _ts: Number(row.end_date),
        x: formatHHmmInTz(SITE_TIMEZONE, fromUnixTime(row.end_date)),
        y: row.battery_soc <= 100 ? row.battery_soc : 100,
      }));

      const futureChartDataForChart = futureChartData.map((point) => ({
        ...point,
        _ts: point?.data?.target_datetime_utc,
      }));

      const behaviorChartRawData = [...historicChartData, ...futureChartDataForChart];

      // De-duplicate the chart array (keep first occurrence, which prefers historic at the intersection)
      const uniqueBehaviorChartData = behaviorChartRawData
        .filter((obj, index, self) => index === self.findIndex((o) => o?.x === obj?.x))
        .toSorted((a, b) => (a?._ts ?? 0) - (b?._ts ?? 0));

      setTodaySoFarData(aggregateRecords(pastRecordsToday));
      setHistoricChartData(historicChartData);
      setBehaviourChartData(uniqueBehaviorChartData);
    }
  }, [
    fetchingBehaviourForecast,
    fetchingTwentyFourHoursOfData,
    twentyFourHoursOfData,
    behaviourForecast,
    loopIsDisabled,
    costWindows,
    costWindowsExport,
  ]);

  useEffect(() => {
    if (!!userMpan) {
      getUserGridCampaigns()
        .then((res) => {
          setUserCampaigns(res);
          getUserGridEvents(tdasCampaignId).then((res) => {
            if (!!res?.length) {
              setUserEvents(res);
              setGridEvent(getNextFutureEvent(res));
            }
          });
        })
        .catch((error) => console.error(error));
    }
  }, [setGridEvent, setUserCampaigns, setUserEvents, userMpan]);

  const onCloseTdasModal = () => {
    setShowTdasModal(false);
  };

  const onDismissTdasModal = () => setHideTdasBanner(true);

  useHandleModalPath('/app/today/tdas', '/app/today', onOpenTdasModal);
  useHandleModalPath('/app/today/success', '/app/today', onOpenTdasModal, MODAL_VIEWS.SUCCESS);
  useHandleModalPath('/app/today/error', '/app/today', onOpenTdasModal, MODAL_VIEWS.ERROR);

  const intersectionX = historicChartData && historicChartData[historicChartData?.length - 1]?.x;
  const intersectionY = (behaviorChartData && behaviorChartData.find((d) => d?.x === intersectionX)?.y) || 0;

  const notEnoughData = fetchingTwentyFourHoursOfData || !twentyFourHoursOfData || !futureRecords?.length;

  return !fetchingTwentyFourHoursOfData ? (
    <div>
      {tdasIsActive && <TdasModal show={showTdasModal} onHide={onCloseTdasModal} />}
      {notEnoughData && (
        <>
          <WarningPanel
            title="Not enough data to show Today's behaviour."
            body={`Please wait 30 minutes for the ${theme.language.appName} calculations to be made.`}
            linkText={`Why does ${theme.language.appName} not have my data yet?`}
            linkAction={() => setShowNoDataModal(true)}
          />

          <InfoModal
            show={showNoDataModal}
            onClose={() => setShowNoDataModal(false)}
            infoContent={
              <div>
                {theme.language.appName} processes a variety of data sources to determine actions that affect your
                battery's state of charge. These data sources update at different intervals—every 5, 15, and 30 minutes.
                As a result, new actions are only taken once all updated data has been gathered, which is why{' '}
                {theme.language.appName} operates on the hour and half-hour.
                <br />
                <br />
                So, when you first sign up, you may need to wait up to 30 minutes for any information to be available.
              </div>
            }
          />
        </>
      )}
      {!userDetails?.has_smart_tariff && !isLoadingUserDetails && (
        <SmartTariffWarning
          linkText="Open tariff recommender."
          linkAction={() => {
            navigate(`/app/tariffs`);
          }}
        />
      )}
      {loopIsDisabled && userDetails?.has_smart_tariff && <DisabledWarning />}
      {hasTariffFetchError && (
        <WarningPanel
          title="Could not load tariff rate data."
          body="Rate information may be unavailable on the chart. Please refresh to try again."
        />
      )}
      {tdasIsActive && !hideTdasBanner && !userMpan && (
        <TurnDownAndSave onShowTdas={onOpenTdasModal} onDismiss={onDismissTdasModal} />
      )}

      <ContentStack>
        {!loopIsDisabled && (
          <RecentBatteryStatus
            latestRecord={latestRecord}
            lastInstruction={lastInstruction}
            nextInstruction={nextInstruction}
          />
        )}
        <TodaySoFar todaySoFarData={todaySoFarData} />
        <FurtherInfo
          intersectionX={intersectionX}
          intersectionY={intersectionY}
          historicChartData={historicChartData}
          behaviorChartData={behaviorChartData}
          futureRecords={futureRecords}
          futureChartSegments={futureChartSegments}
          costWindows={costWindows}
          costWindowsExport={costWindowsExport}
          batteryCapacityKwh={userDetails?.battery_details?.capacity}
          loopIsDisabled={loopIsDisabled}
        />
      </ContentStack>
    </div>
  ) : (
    <div className="pt-3">
      <LoadingCard />
    </div>
  );
};

export default withPageTitle('Your Optimisation', TodayContainer);
